import React, { memo, useCallback, useContext, useRef } from "react";
import { cn } from "@synthesis/shared-utils";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Popover as Popover$1 } from "@base-ui/react/popover";
//#region src/Popover/index.tsx
const contentClassName = "bg-white z-[9999] min-w-[150px] rounded-[14px] shadow-lg focus:outline-none";
const getPortalContainer = () => typeof document !== "undefined" ? document.body : void 0;
const PopoverContext = React.createContext({ closePopover: () => void 0 });
const usePopover = () => useContext(PopoverContext);
function ArrowSvg(props) {
	return /* @__PURE__ */ jsxs("svg", {
		width: "20",
		height: "10",
		viewBox: "0 0 20 10",
		fill: "none",
		...props,
		className: "block",
		children: [
			/* @__PURE__ */ jsx("path", {
				d: "M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z",
				className: "fill-white"
			}),
			/* @__PURE__ */ jsx("path", {
				d: "M8.99542 1.85876C9.75604 1.17425 10.9106 1.17422 11.6713 1.85878L16.5281 6.22989C17.0789 6.72568 17.7938 7.00001 18.5349 7.00001L15.89 7L11.0023 2.60207C10.622 2.2598 10.0447 2.2598 9.66436 2.60207L4.77734 7L2.13171 7.00001C2.87284 7.00001 3.58774 6.72568 4.13861 6.22989L8.99542 1.85876Z",
				className: "fill-white"
			}),
			/* @__PURE__ */ jsx("path", {
				d: "M10.3333 3.34539L5.47654 7.71648C4.55842 8.54279 3.36693 9 2.13172 9H0V8H2.13172C3.11989 8 4.07308 7.63423 4.80758 6.97318L9.66437 2.60207C10.0447 2.25979 10.622 2.2598 11.0023 2.60207L15.8591 6.97318C16.5936 7.63423 17.5468 8 18.5349 8H20V9H18.5349C17.2998 9 16.1083 8.54278 15.1901 7.71648L10.3333 3.34539Z",
				className: "fill-white"
			})
		]
	});
}
const PopoverContentCore = ({ children, closePopover, isArrow }) => /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(PopoverContext.Provider, {
	value: { closePopover },
	children
}), isArrow ? /* @__PURE__ */ jsx(Popover$1.Arrow, {
	className: "data-[side=bottom]:top-[-8px] data-[side=left]:right-[-13px] data-[side=right]:left-[-13px] data-[side=top]:bottom-[-8px] data-[side=left]:rotate-90 data-[side=right]:-rotate-90 data-[side=top]:rotate-180",
	children: /* @__PURE__ */ jsx(ArrowSvg, {})
}) : /* @__PURE__ */ jsx("div", { className: "mt-1" })] });
/**
* Popover из UI Kit.
* @see https://www.figma.com/file/lixEouzSvWhAf04wpHXkIo — РЖД UI Kit
*/
const Popover = memo(({ trigger, children, open, setIsOpen, onOpenAutoFocus, disabled, align = "center", side, disablePortal = false, isArrow = true, disableFocusOutside = false, inlineBlock = false, contentStyles, className }) => {
	const actionsRef = useRef(null);
	const anchorRef = useRef(null);
	const [portalContainer, setPortalContainer] = React.useState(null);
	const popupRef = useRef(null);
	const setAnchorNode = useCallback((node) => {
		anchorRef.current = node;
		if (!disablePortal) {
			setPortalContainer(null);
			return;
		}
		setPortalContainer(node ? node.closest("[role=\"dialog\"]") ?? node : null);
	}, [disablePortal]);
	const closePopover = useCallback(() => {
		actionsRef.current?.close();
	}, []);
	const isControlled = typeof open === "boolean" && typeof setIsOpen === "function";
	if (disabled) return trigger ?? null;
	const hasTrigger = Boolean(trigger);
	return /* @__PURE__ */ jsxs(Popover$1.Root, {
		open: isControlled ? open : void 0,
		onOpenChange: isControlled ? (nextOpen, eventDetails) => {
			if (nextOpen === false && disableFocusOutside && (eventDetails.reason === "outside-press" || eventDetails.reason === "focus-out")) {
				eventDetails.cancel();
				return;
			}
			setIsOpen(nextOpen);
		} : void 0,
		actionsRef,
		children: [/* @__PURE__ */ jsx("div", {
			ref: setAnchorNode,
			className: cn(inlineBlock && "inline-block"),
			style: !hasTrigger ? {
				width: "100%",
				height: 0,
				display: "block"
			} : void 0,
			children: trigger ? /* @__PURE__ */ jsx(Popover$1.Trigger, {
				nativeButton: false,
				render: (triggerProps) => {
					const triggerNode = React.isValidElement(trigger) ? React.cloneElement(trigger, { ...trigger.props.type === void 0 ? { type: "button" } : null }) : trigger;
					return /* @__PURE__ */ jsx("span", {
						...triggerProps,
						className: cn("inline-flex", !inlineBlock && "w-full min-w-0", triggerProps.className),
						children: triggerNode
					});
				}
			}) : null
		}), /* @__PURE__ */ jsx(Popover$1.Portal, {
			container: disablePortal ? portalContainer : getPortalContainer(),
			children: /* @__PURE__ */ jsx(Popover$1.Positioner, {
				side,
				align,
				sideOffset: hasTrigger ? 8 : 12,
				positionMethod: disablePortal ? "fixed" : "absolute",
				anchor: () => anchorRef.current,
				children: /* @__PURE__ */ jsx(Popover$1.Popup, {
					ref: popupRef,
					className: cn(contentClassName, className),
					style: contentStyles,
					initialFocus: onOpenAutoFocus ? (interactionType) => {
						let prevented = false;
						onOpenAutoFocus({ preventDefault: () => {
							prevented = true;
						} });
						return prevented ? false : interactionType === "touch" ? popupRef.current : true;
					} : void 0,
					children: /* @__PURE__ */ jsx(PopoverContentCore, {
						closePopover,
						isArrow,
						children
					})
				})
			})
		})]
	});
});
Popover.displayName = "Popover";
//#endregion
export { Popover, usePopover };

//# sourceMappingURL=index.js.map